/**
 * Attention Surface — boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/attention.schemas.ts`
 * imports and re-exports these to register the routes, and the frontend derives its
 * `AttentionItem` / `AttentionSummary` types from the same objects. See
 * packages/api-types/README.md.
 *
 * Covers:
 *   GET    /landlord/api/attention/items
 *   POST   /landlord/api/attention/items/:taskHash/dismiss
 *   POST   /landlord/api/attention/items/:taskHash/snooze
 *   DELETE /landlord/api/attention/items/:taskHash/preference
 *   GET    /tenant/api/attention/items   (tenant subject — read-only, BR-43)
 *
 * v1 aliases (`/landlord/api/readiness/tasks[...]`) are served by the same handlers —
 * see apps/api/src/routes/manager/attention.routes.ts.
 */
import { z } from 'zod';
import { isoDateTimeString } from '../shared/date.js';
/**
 * Preference shape on an item — null when no preference exists.
 */
export const attentionItemPreferenceSchema = z
    .object({
    action: z.string(),
    snoozedUntil: isoDateTimeString.optional(),
})
    .nullable();
/**
 * Display bands, in the single canonical order every UI renders and counts by.
 */
export const attentionBandSchema = z.enum(['act_now', 'waiting_on_other', 'upcoming', 'backlog']);
export const ATTENTION_BAND_ORDER = attentionBandSchema.options;
/**
 * Nav-badge rule (BR-23): count of act_now items with no dismiss/snooze preference.
 */
export function countActNowItems(items) {
    return items.filter((item) => item.band === 'act_now' && item.preference === null).length;
}
/**
 * Nav-badge severity counts: items of the given severity with no dismiss/snooze preference,
 * across every band. Shown beside the `act_now` count so the nav says how much is required of
 * the landlord and how much is merely recommended.
 */
export function countItemsBySeverity(items, severity) {
    return items.filter((item) => item.severity === severity && item.preference === null).length;
}
/**
 * Individual attention item in the GET response. Extends the v1 readiness task shape with
 * subject/scope/due/blockedBy/magnitude/band (attention-surface iteration 1).
 *
 * `amountCents` and `count` are `.optional()`, never `.default(0)` — BR-12: absent
 * magnitude must not read as zero.
 */
export const attentionItemResponseSchema = z.object({
    taskHash: z.string(),
    providerId: z.string(),
    taskKey: z.string(),
    title: z.string(),
    description: z.string().optional(),
    severity: z.enum(['required', 'recommended', 'optional']),
    severityRationale: z.string().optional(),
    actionUrl: z.string().optional(),
    subject: z.object({ type: z.enum(['manager', 'tenant']), id: z.string() }),
    scope: z
        .object({ type: z.enum(['portfolio', 'property', 'unit', 'lease']), id: z.string() })
        .optional(),
    scopeLabel: z.string().optional(),
    dueAt: isoDateTimeString.optional(),
    overdueSince: isoDateTimeString.optional(),
    statutoryDeadline: isoDateTimeString.optional(),
    blockedBy: z.enum(['me', 'counterparty', 'system']),
    amountCents: z.number().int().optional(),
    count: z.number().int().optional(),
    band: attentionBandSchema,
    preference: attentionItemPreferenceSchema,
    resource: z
        .object({
        type: z.enum(['pending_late_fee', 'bank_transaction', 'appointment_proposal', 'applicant']),
        id: z.string(),
    })
        .optional(),
    suppressible: z.boolean(),
    urgentNow: z.boolean().optional(),
});
/**
 * Summary counts in the GET response.
 */
export const attentionSummarySchema = z.object({
    total: z.number(),
    visible: z.number(),
    dismissed: z.number(),
    snoozed: z.number(),
});
/**
 * GET /attention/items response schema — `degraded` lists provider ids that failed or
 * timed out this request (BR-14, BR-25, DC-4).
 */
export const attentionItemsResponseSchema = z.object({
    items: z.array(attentionItemResponseSchema),
    summary: attentionSummarySchema,
    degraded: z.array(z.string()),
});
/**
 * Tenant attention item — same shape, subject pinned to the tenant, and never
 * preference-bearing or suppressible (BR-43: tenants cannot dismiss/snooze).
 */
export const tenantAttentionItemResponseSchema = attentionItemResponseSchema.extend({
    subject: z.object({ type: z.literal('tenant'), id: z.string() }),
    preference: z.null(),
    suppressible: z.literal(false),
});
/**
 * GET /tenant/api/attention/items response schema.
 */
export const tenantAttentionItemsResponseSchema = z.object({
    items: z.array(tenantAttentionItemResponseSchema),
    summary: attentionSummarySchema,
    degraded: z.array(z.string()),
});
//# sourceMappingURL=index.js.map